import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { AXIS_ACTIONS, BUTTON_ACTIONS } from "@/lib/manualMapping";
import { X, RotateCcw, Save, Gamepad2 } from "lucide-react";

const STD_BUTTON_NAMES = ["A", "B", "X", "Y", "LB", "RB", "LT", "RT", "View", "Menu", "L3", "R3", "D-Haut", "D-Bas", "D-Gauche", "D-Droite"];
const STD_AXIS_NAMES = ["Stick G — X", "Stick G — Y", "Stick D — X", "Stick D — Y"];

export default function ControllerConfig({ open, onClose, profile, isAdmin, gamepadState, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) setDraft(JSON.parse(JSON.stringify(profile)));
  }, [profile, open]);

  if (!open || !draft) return null;

  const setAxis = (idx, action) =>
    setDraft((d) => ({ ...d, axis_mapping: { ...d.axis_mapping, [idx]: action } }));
  const setButton = (idx, action) =>
    setDraft((d) => ({ ...d, button_mapping: { ...d.button_mapping, [idx]: action } }));
  const setSens = (k, v) =>
    setDraft((d) => ({ ...d, sensitivity: { ...d.sensitivity, [k]: v } }));
  const setSpeed = (k, v) =>
    setDraft((d) => ({ ...d, speeds: { ...d.speeds, [k]: v } }));
  const setInvert = (k, v) =>
    setDraft((d) => ({ ...d, invert: { ...d.invert, [k]: v } }));

  const resetDefault = () => {
    setDraft((d) => ({
      ...d,
      axis_mapping: { "0": "yaw", "1": "altitude", "2": "strafe", "3": "pitch" },
      button_mapping: {
        "0": "hover", "1": "land_sim", "2": "camera_mode", "3": "camera_center",
        "4": "camera_zoom_out", "5": "camera_zoom_in", "6": "brake", "7": "throttle",
        "8": "hold_position", "9": "rtl_sim", "10": "slow_mode", "11": "fast_mode",
        "12": "camera_tilt_up", "13": "camera_tilt_down", "14": "camera_pan_left", "15": "camera_pan_right",
      },
      deadzone: 0.12,
      sensitivity: { movement: 1, yaw: 1, altitude: 1, camera: 1, camera_pan: 1, camera_tilt: 1, zoom_speed: 1 },
      speeds: { slow: 3, normal: 6, rapid: 10 },
      invert: { camera: false, altitude: true, camera_pan: false, camera_tilt: false },
    }));
    toast.info("Mapping Xbox Elite par défaut restauré (non sauvegardé)");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/controller-profiles/${draft.id}`, {
        name: draft.name,
        axis_mapping: draft.axis_mapping,
        button_mapping: draft.button_mapping,
        deadzone: draft.deadzone,
        sensitivity: draft.sensitivity,
        speeds: draft.speeds,
        invert: draft.invert,
      });
      toast.success("Profil manette sauvegardé");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(apiErr(e, "Échec de la sauvegarde"));
    } finally {
      setSaving(false);
    }
  };

  const liveAxis = (i) => gamepadState?.axes?.[i] ?? 0;
  const liveBtn = (i) => gamepadState?.buttons?.[i];

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-4" data-testid="controller-config-modal">
      <div className="w-full max-w-3xl max-h-[88vh] flex flex-col bg-[#141414] border border-white/15 rounded-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Gamepad2 size={16} className="text-[#3B82F6]" />
            <h2 className="font-chivo font-black tracking-tight uppercase">Configuration manette</h2>
            {!isAdmin && <span className="font-mono text-[9px] uppercase tracking-wider text-[#F59E0B] border border-[#F59E0B]/40 rounded-sm px-1.5 py-0.5">Lecture seule</span>}
          </div>
          <button data-testid="config-close" onClick={onClose} className="w-7 h-7 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10"><X size={14} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          <div className="flex items-center gap-2 font-mono text-[10px]">
            <span className={`flex items-center gap-1 px-2 py-1 rounded-sm border ${gamepadState?.connected ? "border-[#22C55E]/50 text-[#22C55E]" : "border-white/15 text-[#A3A3A3]"}`}>
              {gamepadState?.connected ? `Connectée : ${gamepadState.id}` : "Aucune manette détectée — branchez votre Xbox Elite (USB/Bluetooth)"}
            </span>
          </div>

          {!isAdmin && (
            <div className="font-mono text-[10px] text-[#A3A3A3] bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2">
              Seul un administrateur peut modifier les mappings. Vous pouvez tester la manette ci-dessous.
            </div>
          )}

          {/* Axes */}
          <Section title="Axes (sticks)">
            {[0, 1, 2, 3].map((i) => (
              <Row key={`ax-${i}`} testid={`axis-row-${i}`} name={STD_AXIS_NAMES[i] || `Axe ${i}`}
                live={<Bar value={liveAxis(i)} />}>
                <select data-testid={`axis-select-${i}`} disabled={!isAdmin} value={draft.axis_mapping?.[i] || ""} onChange={(e) => setAxis(String(i), e.target.value)}
                  className="bg-[#0A0A0A] border border-white/10 rounded-sm font-mono text-[10px] px-2 py-1 text-white disabled:opacity-50">
                  <option value="">—</option>
                  {AXIS_ACTIONS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </Row>
            ))}
          </Section>

          {/* Buttons */}
          <Section title="Boutons">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {Array.from({ length: 16 }).map((_, i) => {
                const b = liveBtn(i);
                return (
                  <Row key={`bt-${i}`} testid={`button-row-${i}`} name={`${i} · ${STD_BUTTON_NAMES[i] || ""}`}
                    live={<span className={`w-2.5 h-2.5 rounded-full ${b?.pressed ? "bg-[#22C55E]" : "bg-white/15"}`} />}>
                    <select data-testid={`button-select-${i}`} disabled={!isAdmin} value={draft.button_mapping?.[i] || ""} onChange={(e) => setButton(String(i), e.target.value)}
                      className="bg-[#0A0A0A] border border-white/10 rounded-sm font-mono text-[10px] px-2 py-1 text-white disabled:opacity-50 w-40">
                      <option value="">—</option>
                      {BUTTON_ACTIONS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                    </select>
                  </Row>
                );
              })}
            </div>
            <p className="font-mono text-[9px] text-[#525252] mt-2">
              Les palettes arrière Xbox Elite (P1–P4) peuvent nécessiter une configuration préalable dans l'app Xbox Accessories pour être exposées au navigateur.
            </p>
          </Section>

          {/* Calibration */}
          <Section title="Calibration & sensibilité">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              <RangeRow label="Deadzone sticks" min={0} max={0.4} step={0.01} value={draft.deadzone} disabled={!isAdmin} onChange={(v) => setDraft((d) => ({ ...d, deadzone: v }))} fmt={(v) => v.toFixed(2)} testid="cfg-deadzone" />
              <RangeRow label="Sensibilité déplacement" min={0.2} max={2} step={0.05} value={draft.sensitivity?.movement} disabled={!isAdmin} onChange={(v) => setSens("movement", v)} testid="cfg-sens-move" />
              <RangeRow label="Sensibilité yaw" min={0.2} max={2} step={0.05} value={draft.sensitivity?.yaw} disabled={!isAdmin} onChange={(v) => setSens("yaw", v)} testid="cfg-sens-yaw" />
              <RangeRow label="Sensibilité altitude" min={0.2} max={2} step={0.05} value={draft.sensitivity?.altitude} disabled={!isAdmin} onChange={(v) => setSens("altitude", v)} testid="cfg-sens-alt" />
              <RangeRow label="Sensibilité caméra pan" min={0.2} max={2} step={0.05} value={draft.sensitivity?.camera_pan ?? draft.sensitivity?.camera} disabled={!isAdmin} onChange={(v) => setSens("camera_pan", v)} testid="cfg-sens-cam-pan" />
              <RangeRow label="Sensibilité caméra tilt" min={0.2} max={2} step={0.05} value={draft.sensitivity?.camera_tilt ?? draft.sensitivity?.camera} disabled={!isAdmin} onChange={(v) => setSens("camera_tilt", v)} testid="cfg-sens-cam-tilt" />
              <RangeRow label="Vitesse zoom" min={0.2} max={3} step={0.1} value={draft.sensitivity?.zoom_speed ?? 1} disabled={!isAdmin} onChange={(v) => setSens("zoom_speed", v)} testid="cfg-zoom-speed" />
              <RangeRow label="Vitesse lente (m/s)" min={1} max={6} step={0.5} value={draft.speeds?.slow} disabled={!isAdmin} onChange={(v) => setSpeed("slow", v)} fmt={(v) => v.toFixed(1)} testid="cfg-speed-slow" />
              <RangeRow label="Vitesse normale (m/s)" min={3} max={10} step={0.5} value={draft.speeds?.normal} disabled={!isAdmin} onChange={(v) => setSpeed("normal", v)} fmt={(v) => v.toFixed(1)} testid="cfg-speed-normal" />
              <RangeRow label="Vitesse rapide (m/s)" min={6} max={16} step={0.5} value={draft.speeds?.rapid} disabled={!isAdmin} onChange={(v) => setSpeed("rapid", v)} fmt={(v) => v.toFixed(1)} testid="cfg-speed-rapid" />
            </div>
            <div className="flex items-center gap-6 mt-3 flex-wrap">
              <Toggle label="Inverser pan caméra" checked={!!draft.invert?.camera_pan} disabled={!isAdmin} onChange={(v) => setInvert("camera_pan", v)} testid="cfg-invert-cam-pan" />
              <Toggle label="Inverser tilt caméra" checked={!!draft.invert?.camera_tilt} disabled={!isAdmin} onChange={(v) => setInvert("camera_tilt", v)} testid="cfg-invert-cam-tilt" />
              <Toggle label="Inversion axe altitude" checked={!!draft.invert?.altitude} disabled={!isAdmin} onChange={(v) => setInvert("altitude", v)} testid="cfg-invert-alt" />
            </div>
          </Section>
        </div>

        {isAdmin && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
            <button data-testid="config-reset" onClick={resetDefault} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10 text-[#A3A3A3]">
              <RotateCcw size={13} /> Réinitialiser défaut Xbox Elite
            </button>
            <button data-testid="config-save" onClick={save} disabled={saving} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-50">
              <Save size={13} /> {saving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] mb-2 border-b border-white/5 pb-1">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ name, live, children, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-1.5">
      <span className="font-mono text-[10px] text-[#A3A3A3] w-28 shrink-0 truncate">{name}</span>
      <span className="w-16 shrink-0 flex items-center">{live}</span>
      <span className="ml-auto">{children}</span>
    </div>
  );
}

function Bar({ value }) {
  const pct = ((value + 1) / 2) * 100;
  return (
    <span className="relative block w-full h-1.5 bg-white/10 rounded-sm">
      <span className="absolute top-1/2 -translate-y-1/2 w-1 h-3 bg-[#3B82F6] rounded-sm" style={{ left: `${pct}%` }} />
    </span>
  );
}

function RangeRow({ label, min, max, step, value, onChange, disabled, fmt, testid }) {
  const v = value ?? min;
  return (
    <div>
      <div className="flex justify-between font-mono text-[10px] text-[#A3A3A3] mb-0.5"><span>{label}</span><span className="text-white">{fmt ? fmt(v) : v.toFixed(2)}</span></div>
      <input data-testid={testid} type="range" min={min} max={max} step={step} value={v} disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#3B82F6] disabled:opacity-40" />
    </div>
  );
}

function Toggle({ label, checked, onChange, disabled, testid }) {
  return (
    <button data-testid={testid} disabled={disabled} onClick={() => onChange(!checked)}
      className="flex items-center gap-2 font-mono text-[10px] text-[#A3A3A3] disabled:opacity-50">
      <span className={`w-8 h-4 rounded-full relative transition-colors ${checked ? "bg-[#3B82F6]" : "bg-white/15"}`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${checked ? "left-4" : "left-0.5"}`} />
      </span>
      {label}
    </button>
  );
}
